import type {OpenCopilotChatEvent} from '@github-ui/copilot-chat-events'
import {DEFERRED_PAYLOAD_DEFAULTS} from '@github-ui/copilot-chat-types'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import type {CopilotImmersivePayload} from '@github-ui/copilot-immersive-v1/routes/payloads'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useQuery} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {lazy, Suspense, useMemo} from 'react'

export type CopilotChatLoaderProps = {
  copilotChatUrl: string
  pendingOpenEvent: OpenCopilotChatEvent | null
  onPendingEventHandled: () => void
  chatAppOpen?: boolean
  onChatAppOpen?: () => void
  onChatAppClose?: () => void
  disableAutoFocus?: boolean
}

// Separate component so the query only fires when shouldLoadCopilotChat becomes true,
// rather than on every page load.
export function CopilotChatLoader({
  copilotChatUrl,
  pendingOpenEvent,
  onPendingEventHandled,
  chatAppOpen,
  onChatAppOpen,
  onChatAppClose,
  disableAutoFocus,
}: CopilotChatLoaderProps) {
  const useChatApp = isFeatureEnabled('copilot_chat_app_embedded')
  const quickChatOwnsDiffEvents = isFeatureEnabled('copilot_chat_app_embedded_owns_diff_events')

  const {data: copilotChatProps, isPending} = useQuery({
    queryKey: ['copilot-chat', copilotChatUrl],
    queryFn: async () => {
      const response = await verifiedFetchJSON(copilotChatUrl)

      if (response.ok) {
        try {
          return (await response.json()) as CopilotImmersivePayload
        } catch {
          throw new Error('Failed to parse copilot chat response.')
        }
      }
      throw new Error('Failed to retrieve copilot chat payload.')
    },
  })

  // When the server provides a deferredPayloadUrl (deferred payload feature flag is on),
  // merge deferred defaults into the lite payload. The actual cache-dependent values
  // will be fetched async when the panel opens.
  const embeddedPayload = useMemo(() => {
    if (!copilotChatProps) return null
    if (copilotChatProps.deferredPayloadUrl) {
      return {
        ...DEFERRED_PAYLOAD_DEFAULTS,
        ...copilotChatProps,
      }
    }
    return copilotChatProps
  }, [copilotChatProps])

  if (isPending || !embeddedPayload) return null

  // Route the pending event to quick chat rather than the old immersive embedded
  // chat when either (a) it carries diff references and quick chat owns diff
  // events (the flag-gated main-branch routing), or (b) the event came from a
  // non-header source such as an in-page or docked-panel action. In both cases
  // the immersive receives no pending event so only one surface reacts.
  const pendingEventReferences =
    pendingOpenEvent?.payload && 'references' in pendingOpenEvent.payload
      ? pendingOpenEvent.payload.references
      : undefined
  const routePendingEventByReferences =
    quickChatOwnsDiffEvents && !!pendingEventReferences && pendingEventReferences.length > 0
  const routePendingEventBySource = pendingOpenEvent != null && pendingOpenEvent.payload.source !== 'header'
  const routePendingEventToQuickChat = routePendingEventByReferences || routePendingEventBySource

  if (useChatApp) {
    return (
      <>
        <Suspense fallback={null}>
          <LazyCopilotImmersiveEmbedded
            payload={embeddedPayload}
            pendingOpenEvent={routePendingEventToQuickChat ? null : pendingOpenEvent}
            onPendingEventHandled={routePendingEventToQuickChat ? () => {} : onPendingEventHandled}
          />
        </Suspense>
        <Suspense fallback={null}>
          <LazyChatAppEmbedded
            ahpConfig={embeddedPayload.ahpConfig}
            apiURL={embeddedPayload.apiURL}
            open={chatAppOpen}
            // While immersive embedded is co-mounted it owns the shared diff and
            // new-thread events; quick chat only takes ownership when this flag
            // flips (i.e. once it replaces immersive), so it's off by default.
            ownsSharedEvents={quickChatOwnsDiffEvents}
            pendingOpenEvent={routePendingEventToQuickChat ? pendingOpenEvent : null}
            onPendingOpenEventHandled={routePendingEventToQuickChat ? onPendingEventHandled : undefined}
            onRequestOpen={onChatAppOpen}
            onClose={onChatAppClose}
            disableAutoFocus={disableAutoFocus}
          />
        </Suspense>
      </>
    )
  }

  return (
    <Suspense fallback={null}>
      <LazyCopilotImmersiveEmbedded
        payload={embeddedPayload}
        pendingOpenEvent={pendingOpenEvent}
        onPendingEventHandled={onPendingEventHandled}
      />
    </Suspense>
  )
}

const LazyCopilotImmersiveEmbedded = lazy(async () => {
  const mod = await import('@github-ui/copilot-immersive-embedded/CopilotImmersiveEmbedded')
  return {default: mod.CopilotImmersiveEmbedded}
})

const LazyChatAppEmbedded = lazy(async () => {
  const mod = await import('@github-ui/copilot-chat-app-embedded/ChatAppEmbeddedPartial')
  return {default: mod.ChatAppEmbeddedPartial}
})
